import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Product } from '../../model/product/product.model';
import { MonitorProviderService } from '../provider/monitor-provider.service';
import { ToastService } from '../toast/toast.service';

/**
 * The one free listing that does not go through the cart (2026-09-21, after the
 * theme/content page): Rhino ships an OS-specific file straight from GitHub
 * (Windows: the .ini, macOS: the .zip with the app), fetched and saved under
 * its own name ("Download now"). It counts as a download in the theme
 * statistics, as the content page's Download did. (Slack went through here as
 * "Copy colors" for a day; it is a zip in the cart now, like every other file.)
 */
@Injectable( {
  providedIn: 'root',
} )
export class DirectDownloadService {

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private monitorProviderService: MonitorProviderService,
  ) {
  }

  /** Rhino: Windows takes the .ini, everything else the .zip (the Mac app), saved under the file's own name */
  downloadRhino( product: Product, platformName: string, zipUrl: string ): void {
    const os = this.detectOs();
    const url = os === 'windows' ? zipUrl.replace( /\.zip$/, '.ini' ) : zipUrl;
    const fileName = decodeURIComponent( url.split( '/' ).pop() ?? 'theme.zip' );
    this.http.get( url, { responseType: 'blob' } ).subscribe( {
      next: ( blob ) => {
        this.saveBlob( blob, fileName );
        this.toastService.show( os === 'windows'
          ? `${ fileName } is downloading. In Rhino, open Options → Appearance → Colors → Import and pick it.`
          : `${ fileName } is downloading. Unzip it and double-click the app inside: it applies the theme and reopens Rhino.` );
        this.record( product, platformName );
      },
      error: () => this.toastService.show( 'The file could not be downloaded. Please try again in a moment.' ),
    } );
  }

  private record( product: Product, platformName: string ): void {
    if ( !product?.key ) return;
    this.monitorProviderService.recordDownload( product.key, platformName ).subscribe( {
      error: ( err ) => console.error( 'Failed to record theme download:', err ),
    } );
  }

  private saveBlob( blob: Blob, fileName: string ): void {
    const objectUrl = URL.createObjectURL( blob );
    const anchor = document.createElement( 'a' );
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild( anchor );
    anchor.click();
    document.body.removeChild( anchor );
    setTimeout( () => URL.revokeObjectURL( objectUrl ), 1000 );
  }

  private detectOs(): 'windows' | 'mac' | 'other' {
    const ua = ( ( navigator.userAgent || '' ) + ' ' + ( navigator.platform || '' ) ).toLowerCase();
    if ( ua.includes( 'win' ) ) return 'windows';
    if ( ua.includes( 'mac' ) || ua.includes( 'iphone' ) || ua.includes( 'ipad' ) ) return 'mac';
    return 'other';
  }
}
